import type { TelemetryFrame, TelemetryListener, TelemetryProvider } from './TelemetryProvider';

/**
 * GeolocationTelemetryProvider — real in-car telemetry from the browser's
 * Geolocation API (the only vehicle signal a Tesla web page can get today).
 *
 * GPS fixes arrive at ~1 Hz; feeding those straight to the drivetrain would
 * step the rpm once per second. The SpeedEstimator dead-reckons between fixes
 * (speed + accel·dt) and eases toward each new fix, so we emit smooth frames at
 * 20 Hz. Quality is 'speed-only': throttle/brake are estimated downstream from
 * acceleration by the VirtualDrivetrain.
 */

export type GpsStatus = 'idle' | 'waiting' | 'ok' | 'stale' | 'denied' | 'unavailable' | 'error';

export interface GpsInfo {
  status: GpsStatus;
  accuracyM: number | null;
  fixGapMs: number | null;
  fixes: number;
  lastError: string | null;
  speedSource: 'doppler' | 'haversine' | null;
}

// MEASURED in Marc's Tesla (v236 bay meter): the car browser delivers ~0.5 Hz — a fix every 2 s, with
// coords.speed populated by the car's own GNSS. Both limits below used to be fixed at a 1 Hz assumption,
// which made the last 0.5 s of every Tesla cycle freeze (prediction ran out) and a single dropped fix
// kill the speed entirely (3 s stale cut). They now scale with the OBSERVED fix gap.
const MAX_FIX_AGE_MS = 3000;      // floor; the real cut is max(this, 2.5 × observed gap)
const PREDICT_MS = 1500;          // floor; the real window is max(this, 1.25 × observed gap)
const MAX_SPEED_KMH = 260;

/** Pure, testable speed/accel estimator. Times in ms. */
export class SpeedEstimator {
  private fixSpeedKmh = 0;
  private fixAt = -1;
  private prev: { t: number; lat: number; lon: number; speedKmh: number } | null = null;
  private est = 0;
  private lastSampleAt = -1;
  // The Tesla browser is not one rate but TWO: ~0.3 Hz standing still, ~10 Hz the moment the car pulls
  // away (measured by Marc in the car). A single averaged gap can't serve both — it lags the transition,
  // which lands exactly on the launch. So we keep the last handful of gaps and read them two ways:
  // the MEDIAN drives responsiveness (it follows a rate change within ~3 fixes) and the MAX drives the
  // safety cut (one slow gap protects the speed from being dropped).
  private gaps: number[] = [];
  accelMs2 = 0;
  speedSource: 'doppler' | 'haversine' | null = null;
  // A STAIRCASE IS NOT A SPEED. The Tesla's browser reports the car's speed in whole km/h, and at
  // 10 Hz once rolling: the same figure five or ten times, then one more. Followed at face value that
  // is a step every few hundred milliseconds, and in first gear one km/h is ~140 rpm, on the needle
  // and in the engine's pitch (Marc: 'en el Tesla cuando subía un km/h subía a trompicón las rpm',
  // against a Mac whose simulator hands over a continuous value). The last few fixes are checked
  // for sitting on whole km/h; a source that does is eased over a longer window and its
  // acceleration is averaged across more fixes, so the estimate ramps through the steps instead
  // of climbing them. A source with real fractions (RaceBox, OBD, the app's fused stream) is not
  // touched: smoothing a fine truth only adds lag.
  private residues: number[] = [];
  private lastFixSpeeds: number[] = [];

  /** Returns false when the fix was rejected. */
  addFix(t: number, lat: number, lon: number, speedMs: number | null): boolean {
    let speedKmh: number | null = null;
    if (speedMs != null && Number.isFinite(speedMs) && speedMs >= 0) {
      speedKmh = speedMs * 3.6;
      this.speedSource = 'doppler';
    } else if (this.prev) {
      const dt = (t - this.prev.t) / 1000;
      if (dt >= 0.3) {
        speedKmh = (haversineM(this.prev.lat, this.prev.lon, lat, lon) / dt) * 3.6;
        this.speedSource = 'haversine';
      }
    }
    if (speedKmh == null) {
      this.prev = { t, lat, lon, speedKmh: this.fixSpeedKmh };
      return false;
    }
    if (speedKmh > MAX_SPEED_KMH) return false;
    this.residues.push(Math.abs(speedKmh - Math.round(speedKmh)));
    if (this.residues.length > 8) this.residues.shift();
    this.lastFixSpeeds.push(speedKmh);
    if (this.lastFixSpeeds.length > 8) this.lastFixSpeeds.shift();
    if (this.prev) {
      const dt = (t - this.prev.t) / 1000;
      // works at 1 Hz (plain GPS) AND at 20 Hz (the app's IMU-fused stream): the smoothing
      // window stays ~0.3 s of real time whatever the fix rate
      if (dt >= 0.04) {
        const a = clamp((speedKmh - this.prev.speedKmh) / 3.6 / dt, -9, 9);
        // a quantised source: the acceleration is averaged over ~1 s of fixes, because one fix's
        // worth of a staircase is either zero or a whole km/h in a tenth of a second
        const alpha = this.fixAt < 0 ? 1 : Math.min(0.65, dt * (this.quantised ? 1 : 3));
        this.accelMs2 = alpha * a + (1 - alpha) * this.accelMs2;
      }
    }
    this.prev = { t, lat, lon, speedKmh };
    this.fixSpeedKmh = speedKmh;
    if (this.fixAt >= 0) {
      this.gaps.push(t - this.fixAt);
      if (this.gaps.length > 5) this.gaps.shift();
    }
    this.fixAt = t;
    if (this.lastSampleAt < 0) this.est = speedKmh;
    return true;
  }

  /**
   * The last fixes all sit on whole km/h AND at least two neighbours repeat the same figure: a
   * staircase, not a speed. Whole numbers alone are not enough - a fast source that happens to
   * pass through whole km/h on every fix is still moving between them.
   */
  get quantised(): boolean {
    if (this.residues.length < 4 || !this.residues.every((r) => r < 0.02)) return false;
    for (let i = 1; i < this.lastFixSpeeds.length; i++) if (this.lastFixSpeeds[i] === this.lastFixSpeeds[i - 1]) return true;
    return false;
  }

  /** Typical gap — follows a rate change (0.3 Hz parked → 10 Hz rolling) within ~3 fixes. */
  get medianGapMs(): number {
    if (!this.gaps.length) return 1500;   // unknown source: assume a slow car browser until proven fast
    const s = [...this.gaps].sort((a, b) => a - b);
    return s[Math.floor(s.length / 2)];
  }

  /** How long a fix stays usable — driven by the WORST recent gap, so one slow fix can't mute the engine. */
  get maxFixAgeMs(): number {
    const worst = this.gaps.length ? Math.max(...this.gaps) : 1500;
    return Math.max(MAX_FIX_AGE_MS, Math.min(8000, worst * 2.5));
  }

  /** Extrapolated sample; null when there is no usable fix. */
  sample(t: number): { speedKmh: number; accelMs2: number } | null {
    if (this.fixAt < 0) return null;
    const age = t - this.fixAt;
    if (age > this.maxFixAgeMs) return null;
    // dead-reckon across the WHOLE gap of a slow source, else the last stretch of every cycle freezes
    const predictCap = Math.max(PREDICT_MS, Math.min(4000, this.medianGapMs * 1.25));
    const predict = Math.min(age, predictCap) / 1000;
    // confidence in a 1-fix-old acceleration decays with the horizon: full for the first second, then
    // fading to a third — without this, a 3 s Tesla gap × 9 m/s² would invent 100 km/h out of nothing
    const conf = predict <= 1 ? 1 : Math.max(0.33, 1 - (predict - 1) * 0.3);
    const delta = clamp(this.accelMs2 * 3.6 * predict * conf, -35, 35);
    const target = Math.max(0, this.fixSpeedKmh + delta);
    const dt = this.lastSampleAt < 0 ? 0.05 : Math.min(0.2, (t - this.lastSampleAt) / 1000);
    this.lastSampleAt = t;
    // a slow source needs the proven smooth ease; a fast one (Tesla rolling at 10 Hz, RaceBox at 25,
    // the app's fused 20 Hz stream) must stay tight — smoothing a fast truth only adds lag
    const g = this.medianGapMs;
    // a quantised fast source gets the slow source's window: the ramp between two whole km/h has to
    // take about as long as the car takes to gain one, and at 10 Hz that is several fixes
    const tau = this.quantised ? Math.max(0.45, g < 150 ? 0.1 : g < 400 ? 0.15 : 0.3) : g < 150 ? 0.1 : g < 400 ? 0.15 : 0.3;
    this.est += (target - this.est) * Math.min(1, dt / tau);
    if (this.est < 0.05) this.est = 0;
    return { speedKmh: this.est, accelMs2: this.accelMs2 };
  }

  fixAgeMs(now: number): number | null {
    return this.fixAt < 0 ? null : now - this.fixAt;
  }

  reset(): void {
    this.fixAt = -1;
    this.prev = null;
    this.gaps = [];
    this.residues = [];
    this.lastFixSpeeds = [];
    this.est = 0;
    this.accelMs2 = 0;
    this.lastSampleAt = -1;
  }
}

export class GeolocationTelemetryProvider implements TelemetryProvider {
  readonly id = 'gps';
  readonly label = 'GPS';
  readonly quality = 'speed-only' as const;

  readonly estimator = new SpeedEstimator();
  private readonly listeners = new Set<TelemetryListener>();
  private watchId: number | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private lastFixAt = 0;
  private posListeners = new Set<(p: { lat: number; lon: number; t: number }) => void>();
  private info: GpsInfo = { status: 'idle', accuracyM: null, fixGapMs: null, fixes: 0, lastError: null, speedSource: null };

  getInfo(): GpsInfo {
    const age = this.estimator.fixAgeMs(performance.now());
    if (this.info.status === 'ok' && age != null && age > this.estimator.maxFixAgeMs) this.info.status = 'stale';
    return { ...this.info, speedSource: this.estimator.speedSource };
  }

  /** Inside the ROARDRIVE phone app the NATIVE GPS feeds the page (no browser permission prompt): the app calls
   *  window.__roarGpsFix({lat, lon, speedMps|null, accuracyM, t}) for every fix. Detected by the app's user agent. */
  static get nativeBridge(): boolean {
    return typeof navigator !== 'undefined' && /ROARDRIVE-App/.test(navigator.userAgent);
  }

  start(): void {
    if (this.watchId != null) return;
    if (GeolocationTelemetryProvider.nativeBridge) {
      this.info.status = 'waiting';
      this.estimator.reset();
      (window as Window & { __roarGpsFix?: (f: { lat: number; lon: number; speedMps?: number | null; accuracyM?: number | null }) => void }).__roarGpsFix = (f) => {
        this.onFix({ coords: { latitude: f.lat, longitude: f.lon, speed: f.speedMps ?? null, accuracy: f.accuracyM ?? 10 } } as GeolocationPosition);
      };
      this.watchId = -1;
      this.timer = setInterval(() => this.emit(), 50);
      return;
    }
    if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
      this.info.status = 'unavailable';
      return;
    }
    this.info.status = 'waiting';
    this.estimator.reset();
    this.watchId = navigator.geolocation.watchPosition(
      (pos) => this.onFix(pos),
      (err) => this.onError(err),
      { enableHighAccuracy: true, maximumAge: 0, timeout: 15000 },
    );
    this.timer = setInterval(() => this.emit(), 50);
  }

  stop(): void {
    if (this.watchId != null && this.watchId >= 0 && 'geolocation' in navigator) navigator.geolocation.clearWatch(this.watchId);
    if (this.watchId === -1) delete (window as Window & { __roarGpsFix?: unknown }).__roarGpsFix;
    this.watchId = null;
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    this.info.status = 'idle';
  }

  subscribe(listener: TelemetryListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Raw GPS position stream (for lap timing). */
  onPosition(cb: (p: { lat: number; lon: number; t: number }) => void): () => void {
    this.posListeners.add(cb);
    return () => this.posListeners.delete(cb);
  }

  private onFix(pos: GeolocationPosition): void {
    const t = performance.now();
    const c = pos.coords;
    const ok = this.estimator.addFix(t, c.latitude, c.longitude, c.speed);
    this.posListeners.forEach((cb) => cb({ lat: c.latitude, lon: c.longitude, t }));
    this.info.fixes++;
    this.info.accuracyM = c.accuracy;
    this.info.fixGapMs = this.lastFixAt ? Math.round(t - this.lastFixAt) : null;
    this.lastFixAt = t;
    if (ok) {
      this.info.status = 'ok';
      this.info.lastError = null;
    }
  }

  private onError(err: GeolocationPositionError): void {
    this.info.lastError = `${err.code}: ${err.message}`;
    this.info.status = err.code === 1 ? 'denied' : err.code === 2 ? 'unavailable' : 'error';
  }

  private emit(): void {
    const s = this.estimator.sample(performance.now());
    if (!s) return;
    const frame: TelemetryFrame = {
      t: performance.now(),
      speedKmh: s.speedKmh,
      accelMs2: s.accelMs2,
      pedal: null,
      brake: null,
      source: 'gps',
    };
    this.listeners.forEach((l) => l(frame));
  }
}

function haversineM(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

function clamp(x: number, lo: number, hi: number): number {
  return x < lo ? lo : x > hi ? hi : x;
}
